import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ParsedUrlQuery } from "querystring";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { proofReportChildId } from "@/components/codesmith/proof/proofReportArtifactId";
import { isProofReportMessage } from "@/components/codesmith/proof/proofReport";
import type { AgentSessionState, WorkspaceArtifactId } from "../types";

/** The visible artifact lives on the URL, e.g. `/sessions/abc?pane=<artifact id>`. */
export const WORKSPACE_PANE_QUERY_PARAM = "pane";

/**
 * GitHub's Proof links land in a layout built around the report:
 * `?pane=proof-report:<child>&focus=proof`. `chat` is the same layout with
 * the chat in front of Proof, which only differs on mobile.
 */
const PROOF_FOCUS_QUERY_PARAM = "focus";
export type ProofFocus = "proof" | "chat";
function isProofPaneId(id: WorkspaceArtifactId): boolean {
  return proofReportChildId(id) !== null || id.startsWith("proof:");
}

/** `{ pane: "proof-report:c1", focus: "proof" }` → `"proof"`; any other pane has no Proof to focus. */
export function proofFocusFromQuery(query: ParsedUrlQuery): ProofFocus | null {
  const pane = workspacePaneIdFromQuery(query[WORKSPACE_PANE_QUERY_PARAM]);
  if (pane === null || !isProofPaneId(pane)) return null;
  const focus = firstQueryValue(query[PROOF_FOCUS_QUERY_PARAM]);
  return focus === "proof" || focus === "chat" ? focus : null;
}

/**
 * A session nobody chatted in whose transcript holds only mirrored Proof
 * reports (e.g. the owner a PR footer Proof click creates) has no chat for
 * the report to sit beside, so `proof` focus covers the row. An autofix-only
 * transcript keeps its column, and `chat` keeps the chat the reader brought
 * forward.
 */
export function proofFocusCoversChat(focus: ProofFocus | null, session: Pick<AgentSessionState, "hasManualUserMessage" | "chatMessages"> | null): boolean {
  return focus === "proof" && session?.hasManualUserMessage === false && session.chatMessages.every(isProofReportMessage);
}

/**
 * `({ org: "acme", sessionId: "ses_1" }, "asset-1")` → `"/acme/sessions/ses_1?pane=asset-1"`.
 * Built on the session on screen rather than the URL, which lags a session
 * switch; without a session there is nothing to link to.
 */
export function workspacePaneHref({
  org,
  sessionId
}: {
  org: string | undefined;
  sessionId?: string;
}, id: WorkspaceArtifactId): string | undefined {
  if (sessionId === undefined) return undefined;
  const orgSegment = org === undefined ? "" : `/${encodeURIComponent(org)}`;
  return `${orgSegment}/sessions/${encodeURIComponent(sessionId)}?${WORKSPACE_PANE_QUERY_PARAM}=${encodeURIComponent(id)}`;
}
function firstQueryValue(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}
export function workspacePaneIdFromQuery(value: string | string[] | undefined): WorkspaceArtifactId | null {
  const raw = firstQueryValue(value);
  return raw === undefined || raw === "" ? null : raw;
}
interface PendingPane {
  id: WorkspaceArtifactId | null;
  url: string;
  sequence: number;
}
interface PendingFocus {
  focus: ProofFocus | null;
  url: string;
}
function pathnameOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}

/** Shallow push or replace; `abandon` runs when Next drops or rejects the navigation. */
function navigateShallow(router: NonNullable<ReturnType<typeof useRouter>>, url: string, push: boolean, abandon: () => void) {
  const options = {
    shallow: true,
    scroll: false
  };
  const navigate = push ? router.push(url, undefined, options) : router.replace(url, undefined, options);
  void Promise.resolve(navigate).then(completed => {
    if (!completed) abandon();
  }).catch(abandon);
}
function currentUrlWith(params: URLSearchParams): string {
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
}
export interface WorkspacePaneUrl {
  paneId: WorkspaceArtifactId | null;
  setPaneId: (id: WorkspaceArtifactId | null, options: {
    replace?: boolean;
    reveal: boolean;
  }) => void;
  /** Null whenever no pane is linked: closing the pane also ends focus. */
  focus: ProofFocus | null;
  /**
   * `push` adds a history entry, so browser back undoes it; returning to
   * `proof` from a pushed `chat` goes back through that entry.
   */
  setFocus: (focus: ProofFocus | null, options?: {
    push?: boolean;
  }) => void;
}

/**
 * URL-backed pane id with an optimistic value while Next's shallow route
 * settles. The URL only speaks for `sessionId`'s route: while the shell
 * still shows the previous session's URL after a switch, it reads as bare
 * and writes are dropped, so a stale `?pane=` cannot reshape the next
 * session's saved arrangement.
 */
export function useWorkspacePaneUrl(t0) {
  const $ = _c(23);
  const sessionId = t0 === undefined ? null : t0;
  const router = useRouter();
  const [localId, setLocalId] = useState(null);
  const [localFocus, setLocalFocus] = useState(null);
  const [pending, setPending] = useState();
  const [pendingFocus, setPendingFocus] = useState();
  const pushedFocusUrl = useRef(null);
  const sequence = useRef(0);
  let t1;
  if ($[0] !== router) {
    t1 = router ? workspacePaneIdFromQuery(router.query[WORKSPACE_PANE_QUERY_PARAM]) : null;
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const routeId = t1;
  let routePathname;
  let t2;
  if ($[2] !== router || $[3] !== sessionId) {
    routePathname = router ? pathnameOf(router.asPath) : null;
    t2 = routePathname === null || sessionId === null || routePathname.split("/").includes(sessionId);
    $[2] = router;
    $[3] = sessionId;
    $[4] = routePathname;
    $[5] = t2;
  } else {
    routePathname = $[4];
    t2 = $[5];
  }
  const routeShowsSession = t2;
  const pendingSettled = pending !== undefined && (routeId === pending.id || routePathname !== null && routePathname !== pathnameOf(pending.url));
  if (pendingSettled) {
    setPending(undefined);
  }
  const activePending = pendingSettled ? undefined : pending;
  let t3;
  if ($[6] !== localFocus || $[7] !== router) {
    t3 = router ? proofFocusFromQuery(router.query) : localFocus;
    $[6] = localFocus;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const routeFocus = t3;
  const focusSettled = pendingFocus !== undefined && (routeFocus === pendingFocus.focus || routePathname !== null && routePathname !== pathnameOf(pendingFocus.url));
  if (focusSettled) {
    setPendingFocus(undefined);
  }
  const activePendingFocus = focusSettled ? undefined : pendingFocus;
  let t4;
  let t5;
  if ($[9] !== router) {
    t4 = () => {
      if (!router?.events) {
        return;
      }
      const settle = () => {
        setPending(undefined);
        setPendingFocus(undefined);
      };
      const settleUrl = url => {
        setPending(current => current?.url === url ? undefined : current);
        setPendingFocus(current_0 => current_0?.url === url ? undefined : current_0);
      };
      const settleError = (_error, url_0) => settleUrl(url_0);
      router.events.on("routeChangeError", settleError);
      window.addEventListener("popstate", settle);
      return () => {
        router.events.off("routeChangeError", settleError);
        window.removeEventListener("popstate", settle);
      };
    };
    t5 = [router];
    $[9] = router;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  const paneId = router ? !routeShowsSession ? null : activePending ? activePending.id : routeId : localId;
  let t6;
  if ($[12] !== routeShowsSession || $[13] !== router) {
    t6 = (next, options) => {
      if (!router) {
        setLocalId(next);
        return;
      }
      if (!routeShowsSession) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const openInUrl = params.has(WORKSPACE_PANE_QUERY_PARAM);
      if (next === null) {
        params.delete(WORKSPACE_PANE_QUERY_PARAM);
        params.delete(PROOF_FOCUS_QUERY_PARAM);
      } else {
        params.set(WORKSPACE_PANE_QUERY_PARAM, next);
      }
      const showsProof = next !== null && isProofPaneId(next);
      const leavesFocus = next !== null && !showsProof && params.has(PROOF_FOCUS_QUERY_PARAM);
      const revealsProof = options.reveal && showsProof && params.get(PROOF_FOCUS_QUERY_PARAM) === "chat";
      if (leavesFocus) {
        params.delete(PROOF_FOCUS_QUERY_PARAM);
      }
      if (revealsProof) {
        params.set(PROOF_FOCUS_QUERY_PARAM, "proof");
      }
      const url_1 = currentUrlWith(params);
      const nextSequence = sequence.current = sequence.current + 1;
      setPending({
        id: next,
        url: url_1,
        sequence: nextSequence
      });
      if (leavesFocus || revealsProof) {
        pushedFocusUrl.current = null;
        setPendingFocus({
          focus: revealsProof ? "proof" : null,
          url: url_1
        });
      }
      navigateShallow(router, url_1, !openInUrl && next !== null && !options.replace, () => {
        setPending(current_1 => current_1?.sequence === nextSequence ? undefined : current_1);
        setPendingFocus(current_2 => current_2?.url === url_1 ? undefined : current_2);
      });
    };
    $[12] = routeShowsSession;
    $[13] = router;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const setPaneId = t6;
  const focus = paneId === null ? null : router ? activePendingFocus ? activePendingFocus.focus : routeFocus : localFocus;
  let t7;
  if ($[15] !== routeShowsSession || $[16] !== router) {
    t7 = (next_0, options_0) => {
      if (!router) {
        setLocalFocus(next_0);
        return;
      }
      if (!routeShowsSession) {
        return;
      }
      if (next_0 === "proof" && pushedFocusUrl.current === window.location.href) {
        pushedFocusUrl.current = null;
        router.back();
        return;
      }
      const params_0 = new URLSearchParams(window.location.search);
      if (next_0 === null) {
        params_0.delete(PROOF_FOCUS_QUERY_PARAM);
      } else {
        params_0.set(PROOF_FOCUS_QUERY_PARAM, next_0);
      }
      const url_2 = currentUrlWith(params_0);
      setPendingFocus({
        focus: next_0,
        url: url_2
      });
      if (options_0?.push) {
        pushedFocusUrl.current = new URL(url_2, window.location.href).href;
      }
      navigateShallow(router, url_2, options_0?.push === true, () => setPendingFocus(current_3 => current_3?.url === url_2 ? undefined : current_3));
    };
    $[15] = routeShowsSession;
    $[16] = router;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const setFocus = t7;
  let t8;
  if ($[18] !== focus || $[19] !== paneId || $[20] !== setFocus || $[21] !== setPaneId) {
    t8 = {
      paneId,
      setPaneId,
      focus,
      setFocus
    };
    $[18] = focus;
    $[19] = paneId;
    $[20] = setFocus;
    $[21] = setPaneId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}